import {
  getProAdminRefundRequests,
  type ProAdminRefundListQuery,
  queryKeys,
  useAuthStore,
} from "@dutyon/apis";
import {
  Alert,
  Button,
  Card,
  Group,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  TitleContainer,
} from "@dutyon/ui-kit";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router-dom";
import {
  executionStatuses,
  prepareAdminRefundFilters,
  readAdminRefundList,
  statuses,
} from "./model";

const empty = { status: "", executionStatus: "", inquiryId: "", orderId: "", from: "", to: "" };
export function ProRefundListPage() {
  const actor = useAuthStore((state) => state.user);
  const token = useAuthStore((state) => state.token);
  if (!token || !actor || actor.role !== "SUPER_ADMIN")
    return <Alert color="red">최고 관리자만 환불 요청을 조회할 수 있습니다.</Alert>;
  return <RefundList key={actor.sub} owner={actor.sub} />;
}
function RefundList({ owner }: { owner: string }) {
  const [draft, setDraft] = useState(empty),
    [filters, setFilters] = useState<ProAdminRefundListQuery>({}),
    [message, setMessage] = useState("");
  const history = useInfiniteQuery({
    queryKey: queryKeys.pro.adminRefundRequests(owner, filters),
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam }) => {
      let changed = false;
      const unsubscribe = useAuthStore.subscribe((state) => {
        if (state.user?.sub !== owner || state.user.role !== "SUPER_ADMIN") changed = true;
      });
      try {
        const result = await getProAdminRefundRequests(owner, {
          ...filters,
          ...(pageParam ? { cursor: pageParam } : {}),
        });
        const state = useAuthStore.getState();
        if (changed || state.user?.sub !== owner || state.user.role !== "SUPER_ADMIN")
          throw new Error("계정이 변경되었습니다.");
        return readAdminRefundList(result, pageParam);
      } finally {
        unsubscribe();
      }
    },
    getNextPageParam: (page) => page.nextCursor ?? undefined,
    retry: false,
    staleTime: 0,
    refetchOnMount: "always",
  });
  const unavailable = history.isError && !history.isFetchNextPageError;
  const rows = unavailable
    ? []
    : [
        ...new Map(
          (history.data?.pages.flatMap((page) => page.items) ?? []).map((row) => [row.id, row]),
        ).values(),
      ];
  return (
    <TitleContainer title="Pro 환불 요청">
      <Stack>
        <Text size="sm">
          문의 답변은 환불 승인이 아닙니다. 상세에서 승인·반려 기록과 환불 진행 상태·확정 기록을
          확인해 주세요.
        </Text>
        <Group align="end">
          <Select
            label="환불 상태"
            data={[
              { value: "", label: "전체 상태" },
              ...Object.entries(statuses).map(([value, label]) => ({ value, label })),
            ]}
            value={draft.status}
            allowDeselect={false}
            onChange={(value) => setDraft({ ...draft, status: value ?? "" })}
          />
          <TextInput
            label="문의 번호"
            value={draft.inquiryId}
            onChange={(event) => setDraft({ ...draft, inquiryId: event.currentTarget.value })}
          />
          <Select
            label="환불 실행 상태"
            data={[
              { value: "", label: "전체 실행 상태" },
              ...Object.entries(executionStatuses).map(([value, label]) => ({ value, label })),
            ]}
            value={draft.executionStatus}
            allowDeselect={false}
            onChange={(value) => setDraft({ ...draft, executionStatus: value ?? "" })}
          />
          <TextInput
            label="주문 번호"
            value={draft.orderId}
            onChange={(event) => setDraft({ ...draft, orderId: event.currentTarget.value })}
          />
          <TextInput
            label="접수 시작일"
            type="date"
            value={draft.from}
            onChange={(event) => setDraft({ ...draft, from: event.currentTarget.value })}
          />
          <TextInput
            label="접수 종료일"
            type="date"
            value={draft.to}
            onChange={(event) => setDraft({ ...draft, to: event.currentTarget.value })}
          />
          <Button
            onClick={() => {
              try {
                setFilters(prepareAdminRefundFilters(draft));
                setMessage("");
              } catch (error) {
                setMessage(error instanceof Error ? error.message : "조회 조건을 확인해 주세요.");
              }
            }}
          >
            필터 적용
          </Button>
          <Button
            variant="subtle"
            onClick={() => {
              setDraft(empty);
              setFilters({});
              setMessage("");
            }}
          >
            필터 초기화
          </Button>
          <Button
            variant="light"
            disabled={history.isFetching}
            onClick={() => void history.refetch()}
          >
            환불 목록 다시 조회
          </Button>
        </Group>
        {message && <Alert color="red">{message}</Alert>}
        {history.isPending && <Text role="status">환불 요청을 불러오는 중입니다.</Text>}
        {history.isError && (
          <Alert color="red" title="환불 목록 조회 실패">
            {history.isFetchNextPageError
              ? "다음 요청을 불러오지 못했습니다. 더 보기로 다시 시도해 주세요."
              : "목록을 확인하지 못했습니다. 다시 조회해 주세요."}
          </Alert>
        )}
        {!history.isPending && !unavailable && rows.length === 0 && (
          <Text>조회 조건에 맞는 환불 요청이 없습니다.</Text>
        )}
        {rows.length > 0 && (
          <Card withBorder>
            <Table.ScrollContainer minWidth={650}>
              <Table>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>요청 번호</Table.Th>
                    <Table.Th>상태</Table.Th>
                    <Table.Th>실행 상태</Table.Th>
                    <Table.Th>접수일</Table.Th>
                    <Table.Th>관련 문의</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {rows.map((row) => (
                    <Table.Tr key={row.id}>
                      <Table.Td>
                        <Link to={`/pro-billing/refunds/${encodeURIComponent(row.id)}`}>
                          {row.id}
                        </Link>
                      </Table.Td>
                      <Table.Td>{statuses[row.status]}</Table.Td>
                      <Table.Td>
                        {row.executionStatus ? executionStatuses[row.executionStatus] : "미제공"}
                      </Table.Td>
                      <Table.Td>
                        {new Intl.DateTimeFormat("ko-KR", {
                          timeZone: "Asia/Seoul",
                          dateStyle: "short",
                          timeStyle: "short",
                        }).format(new Date(row.requestedAt))}
                      </Table.Td>
                      <Table.Td>
                        <Button
                          component={Link}
                          to={`/inquiries/${encodeURIComponent(row.inquiryId)}`}
                          variant="subtle"
                        >
                          문의 확인
                        </Button>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
          </Card>
        )}
        {!unavailable && history.hasNextPage && (
          <Button
            disabled={history.isFetching}
            loading={history.isFetchingNextPage}
            onClick={() => void history.fetchNextPage()}
          >
            환불 목록 더 보기
          </Button>
        )}
      </Stack>
    </TitleContainer>
  );
}
